import { toast } from "react-toastify";
import { getSchema } from "./form-validations";
import {
  ChangedFields,
  initialChangedFields,
  initialState,
  ListingData,
} from "./types";
import { create } from "zustand";
import { ApiError } from "next/dist/server/api-utils";
import isEqual from "fast-deep-equal";
import { env } from "@/lib/env";

export const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const LISTINGSENDPOINTURL = env.NEXT_LISTINGS_API_URL;

export interface EdittingStore {
  initialListingData: ListingData;
  listing: ListingData;
  changedFields: ChangedFields;
  isHydrated: boolean;
  validationErrors: Partial<Record<keyof ListingData, string>>;
  setField: <K extends keyof ListingData>(
    key: K,
    value: ListingData[K],
  ) => void;
  // setNestedFields: (
  //   section: keyof ListingData,
  //   key: string,
  //   value: any
  // ) => void;
  validateFields: () => {
    validationErrors: Record<string, string | undefined>;
    firstErrorKey?: string;
  };
  initListing: (
    listingId: string,
    sessionId: string | null,
    locale: string,
  ) => Promise<void>;
}

export const useEdittingStore = create<EdittingStore>()((set, get) => ({
  initialListingData: initialState,
  listing: initialState,
  changedFields: initialChangedFields,
  isHydrated: false,
  validationErrors: {},
  setField: (key, value) =>
    set((state) => {
      if (state.listing === null) {
        return state;
      }
      const newState = { ...state.listing, [key]: value };
      const validation = getSchema(
        newState.type,
        newState.purpose,
        state.listing.listing_type,
      ).safeParse(newState);
      const errorObj = validation.error?.format() as any;
      let errorMessage = validation.error?.issues.find(
        (issue) => issue.path[0] === key,
      )?.message;
      if (errorMessage === "Required") {
        errorMessage = undefined;
      }
      const isFieldChanged =
        get().initialListingData[key] !== value ? true : false;
      return {
        listing: { ...state.listing, [key]: value },
        validationErrors: {
          ...state.validationErrors,
          [key]: errorMessage,
        },
        changedFields: {
          ...state.changedFields,
          [key]: isFieldChanged,
        },
      };
    }),
  // setNestedFields: (section, key, value) =>
  //   set((state) => {
  //     const { listing, initialListingData, validationErrors, changedFields } =
  //       state;
  //     if (!listing) return state;

  //     const updatedSection = {
  //       ...(listing[section] as Record<string, any>),
  //       [key]: value,
  //     };

  //     const newState = {
  //       ...listing,
  //       [section]: updatedSection,
  //     };

  //     const validation = getSchema(
  //       newState.type,
  //       newState.purpose,
  //       state.listing.listing_type
  //     ).safeParse(newState);
  //     const errorObj = validation.error?.format() as any;
  //     let errorMessage = validation.error?.issues.find(
  //       (issue) => issue.path[0] === key
  //     )?.message;
  //     if (errorMessage === "Required") {
  //       errorMessage = undefined;
  //     }
  //     const hasChanged = !isEqual(initialListingData[section], updatedSection);
  //     return {
  //       listing: newState,
  //       validationErrors: {
  //         ...validationErrors,
  //         [key]: errorMessage,
  //       },
  //       changedFields: {
  //         ...changedFields,
  //         [section]: {
  //           ...(changedFields[section] as Record<string, any>),
  //           [key]: hasChanged,
  //         },
  //       },
  //     };
  //   }),
  validateFields: () => {
    let validationErrors: Record<string, string | undefined> = {};
    let firstErrorKey: string | undefined;
    set((state) => {
      if (state.listing === null) {
        return state;
      }

      const validation = getSchema(
        state.listing.type,
        state.listing.purpose,
        state.listing.listing_type,
      ).safeParse(state.listing);

      if (!validation.success) {
        validation.error.issues.forEach((issue, idx) => {
          const field = issue.path[0] as string;
          validationErrors[issue.path[0]] = issue.message;
          if (idx === 0) firstErrorKey = field;
        });
      }

      return { ...state, validationErrors: validationErrors };
    });
    return { validationErrors, firstErrorKey };
  },
  initListing: async (listingId, sessionId, locale) => {
    set((state) => ({
      ...state,
      listing: initialState,
      isHydrated: false,
    }));
    if (!sessionId) {
      toast("Please reauthenticate before performing this action.", {
        type: "error",
      });
      return;
    }

    try {
      const response = await fetch(
        `${LISTINGSENDPOINTURL}/getListingForEditting?listing_id=${listingId}&locale=${locale}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionId,
          },
        },
      );

      if (!response.ok) {
        const apiErr = (await response.json()) as ApiError;
        throw new Error(apiErr.message);
      }

      const data: ListingData = await response.json();

      set((state) => ({
        ...state,
        initialListingData: data,
        listing: data,
        isHydrated: true,
      }));
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      toast(
        errorMessage === "Failed to fetch"
          ? "Unable to initialize listing."
          : errorMessage,
        {
          type: "error",
        },
      );
    }
  },
}));
